import { QueryClient, QueryFunction } from "@tanstack/react-query";

// Original API Request Funktion mit Authentifizierungslogik
export async function apiRequest(
  method: string,
  url: string,
  data?: unknown | undefined,
): Promise<Response> {
  const headers: Record<string, string> = {
    ...(data ? { "Content-Type": "application/json" } : {}),
    'Accept': 'application/json',
    'X-Requested-With': 'XMLHttpRequest',
  };
  
  const res = await fetch(url, {
    method,
    headers,
    body: data ? JSON.stringify(data) : undefined,
    credentials: "include",
  });

  // Session-Aktivität aktualisieren
  if (res.ok) {
    localStorage.setItem('speiseguru_last_activity', Date.now().toString());
  }

  await throwIfResNotOk(res);
  return res;
}

// Vereinfachter API-Request für Cache-Abfragen
export const simpleApiRequest = async (url: string, options: RequestInit = {}) => {
  const headers = {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
    ...(options.headers || {})
  };
  
  const response = await fetch(url, {
    ...options,
    headers,
    credentials: 'include'
  });
  
  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }
  
  return await response.json();
};

/**
 * P0: Sichere Response-Parsing-Funktion
 * Verhindert JSON-Parse-Errors bei leeren Bodies oder falschen Content-Types
 */
export async function safeParseResponse<T = any>(response: Response): Promise<T | null> {
  // Prüfe Content-Length: Wenn 0, gibt es keinen Body
  const contentLength = response.headers.get("content-length");
  if (contentLength === "0") {
    return null;
  }

  // Prüfe Content-Type
  const contentType = response.headers.get("content-type");
  
  // Wenn kein Content-Type oder leerer Body, return null
  if (!contentType) {
    // Versuche Text zu lesen, falls vorhanden
    try {
      const text = await response.clone().text();
      if (!text || text.trim() === "") {
        return null;
      }
      // Versuche JSON zu parsen, auch wenn kein Content-Type gesetzt ist
      try {
        return JSON.parse(text) as T;
      } catch {
        return text as unknown as T;
      }
    } catch {
      return null;
    }
  }

  // JSON parsen
  if (contentType.includes("application/json")) {
    try {
      const text = await response.text();
      if (!text || text.trim() === "") {
        return null;
      }
      return JSON.parse(text) as T;
    } catch (error) {
      console.error("JSON-Parse-Fehler:", error);
      throw new Error(`Fehler beim Parsen der JSON-Antwort: ${error instanceof Error ? error.message : "Unbekannter Fehler"}`);
    }
  }

  // Text parsen
  if (contentType.includes("text/")) {
    try {
      const text = await response.text();
      return (text || null) as unknown as T;
    } catch (error) {
      console.error("Text-Parse-Fehler:", error);
      return null;
    }
  }

  // Fallback: Versuche Text zu lesen
  try {
    const text = await response.text();
    if (!text || text.trim() === "") {
      return null;
    }
    // Versuche JSON zu parsen
    try {
      return JSON.parse(text) as T;
    } catch {
      return text as unknown as T;
    }
  } catch {
    return null;
  }
}

async function throwIfResNotOk(res: Response) {
  if (!res.ok) {
    try {
      // P0: Nutze safeParseResponse für robustes Error-Parsing
      const errorData = await safeParseResponse<{ message?: string; error?: string }>(res);
      const errorMessage = errorData?.message || errorData?.error || res.statusText || `HTTP ${res.status}`;
      throw new Error(errorMessage);
    } catch (error) {
      if (error instanceof Error) {
        throw error; // Wenn bereits eine Error-Instanz erstellt wurde, wirf diese
      }
      // Fallback: Original-Status und Statustext
      throw new Error(`Fehler: ${res.status} ${res.statusText}`);
    }
  }
}

type UnauthorizedBehavior = "returnNull" | "throw";
export const getQueryFn: <T>(options: {
  on401: UnauthorizedBehavior;
}) => QueryFunction<T> =
  ({ on401: unauthorizedBehavior }) =>
  async ({ queryKey }) => {
    const url = queryKey[0] as string;
    
    const res = await fetch(url, {
      credentials: "include",
      headers: {
        'Accept': 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      }
    });

    // Session-Aktivität aktualisieren
    if (res.ok) {
      localStorage.setItem('speiseguru_last_activity', Date.now().toString());
    }

    if (unauthorizedBehavior === "returnNull" && res.status === 401) {
      return null;
    }

    await throwIfResNotOk(res);
    const data = await res.json();
    return data;
  };

/**
 * Optimized config for quasi-static data (cuisines, skills, etc.)
 * - 24h staleTime: Data stays fresh for a full day
 * - 7d gcTime: Keep in memory for a week (even when unused)
 * - No refetch on focus/mount/reconnect: Treat as truly static
 */
export const STATIC_DATA_CONFIG = {
  staleTime: 24 * 60 * 60 * 1000, // 24h
  gcTime: 7 * 24 * 60 * 60 * 1000, // 7d
  refetchOnWindowFocus: false,
  refetchOnMount: false,
  refetchOnReconnect: false,
};

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      queryFn: getQueryFn({ on401: "throw" }),
      staleTime: 5 * 60 * 1000, // Daten sind 5 Minuten gültig (statt sofort veraltet)
      gcTime: 10 * 60 * 1000, // Cache für 10 Minuten im Speicher halten
      refetchOnWindowFocus: false, // Kein automatisches Refetch bei Focus (verhindert unnötige Requests)
      refetchOnMount: false, // Nutze Cache, wenn Daten noch fresh sind (staleTime)
      refetchOnReconnect: true, // Bei Reconnect refetchen (sinnvoll)
      retry: 1, // Bei Fehlern nur einmal wiederholen
      retryDelay: attemptIndex => Math.min(1000 * 2 ** attemptIndex, 30000),
    },
    mutations: {
      retry: false,
      // KEINE globale Invalidierung mehr - jede Mutation invalidiert selektiv
      // Siehe z.B. RestaurantFormGeneral.tsx, RestaurantFormMedia.tsx etc.
    },
  },
});
